import React, { useEffect, useMemo, useState } from 'react'
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table'
import { IconContext } from 'react-icons'
import { FaAngleUp, FaAngleDown } from 'react-icons/fa'
import { rankItem } from '@tanstack/match-sorter-utils'

const getHeaderProps = (header, sortable) => {
    if (sortable && header.column.getCanSort()) {
        return { onClick: header.column.getToggleSortingHandler(), style: { cursor: 'pointer' } }
    }
    return {}
}

const fuzzyTextFilterFn = (row, columnId, filterValue) => {
    const itemRank = rankItem(row.getValue(columnId), filterValue)
    return itemRank.passed
}

const DefaultColumnFilter = ({ column }) => {
    return (
        <input
            className="input is-small is-size-7"
            value={column.getFilterValue() ?? ''}
            onChange={e => {
                column.setFilterValue(e.target.value || undefined)
            }}
            placeholder={`Filter...`}
        />
    )
}

const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = React.useRef()
        const resolvedRef = ref || defaultRef

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate
        }, [resolvedRef, indeterminate])

        return (
            <span className="icon">
                <input type="checkbox" ref={resolvedRef} {...rest} />
            </span>
        )
    }
)

const selectionColumn = {
    id: 'selection',
    header: ({ table }) => (
        <div>
            <IndeterminateCheckbox
                checked={table.getIsAllRowsSelected()}
                indeterminate={table.getIsSomeRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
            />
        </div>
    ),
    cell: ({ row }) => (
        <div>
            <IndeterminateCheckbox
                checked={row.getIsSelected()}
                indeterminate={row.getIsSomeSelected()}
                onChange={row.getToggleSelectedHandler()}
            />
        </div>
    ),
}

const SimpleTable = ({
    columns,
    data,
    sortable = false,
    filterable = false,
    selectable = false,
    onSelect = null,
}) => {
    const [rowSelection, setRowSelection] = useState({})
    const [columnFilters, setColumnFilters] = useState([])

    const allColumns = useMemo(() => {
        if (selectable) {
            return [selectionColumn, ...columns]
        }
        return columns
    }, [columns, selectable])

    const table = useReactTable({
        columns: allColumns,
        data,
        state: {
            rowSelection,
            columnFilters,
        },
        onRowSelectionChange: setRowSelection,
        onColumnFiltersChange: setColumnFilters,
        enableRowSelection: selectable,
        enableSorting: sortable,
        enableFilters: filterable,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: sortable ? getSortedRowModel() : undefined,
        getFilteredRowModel: filterable ? getFilteredRowModel() : undefined,
        filterFns: { fuzzyText: fuzzyTextFilterFn },
        defaultColumn: {
            filterFn: 'includesString',
        },
    })

    useEffect(() => {
        if (selectable) {
            const selectedIdx = Object.keys(rowSelection)
            const associatedRows = data.filter((x, idx) =>
                selectedIdx.includes(idx.toString())
            )
            onSelect(associatedRows)
        }
    }, [rowSelection])

    useEffect(() => {
        if (selectable) {
            setRowSelection({})
        }
    }, [columnFilters])

    return (
        <div className="gridWrapper">
            <table className="table is-fullwidth is-striped">
                <thead className={'sticky-top'}>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    className="is-size-7 vertical-align"
                                >
                                    <div className="explorerTableHeader simpleTableHeader">
                                        <div
                                            className="nameAndSort"
                                            {...getHeaderProps(header, sortable)}
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            <span className="explorerTableArrow simpleTableArrow">
                                                {header.column.getIsSorted() ? (
                                                    header.column.getIsSorted() === 'desc' ? (
                                                        <IconContext.Provider
                                                            value={{
                                                                className: 'has-text-link',
                                                                size: '1.1rem',
                                                            }}
                                                        >
                                                            <FaAngleDown />
                                                        </IconContext.Provider>
                                                    ) : (
                                                        <IconContext.Provider
                                                            value={{
                                                                className: 'has-text-link',
                                                                size: '1.1rem',
                                                            }}
                                                        >
                                                            <FaAngleUp />
                                                        </IconContext.Provider>
                                                    )
                                                ) : (
                                                    ''
                                                )}
                                            </span>
                                        </div>
                                        {filterable && header.column.getCanFilter() ? (
                                            <div className="filter">
                                                <DefaultColumnFilter column={header.column} />
                                            </div>
                                        ) : null}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default SimpleTable
export { getHeaderProps, fuzzyTextFilterFn, DefaultColumnFilter, IndeterminateCheckbox, selectionColumn }
